import { z } from 'zod';
import {
  AccuracySchema,
  AltitudeSchema,
  BatteryLevelSchema,
  HeadingSchema,
  IsoDateTimeSchema,
  LatitudeSchema,
  LongitudeSchema,
  SpeedSchema,
} from '../common/primitives';
import { LocationProvider, MotionType, NetworkType, RejectReason } from '../enums/index';

/** One captured fix, as the device sends it. */
export const PointSchema = z
  .object({
    /** Device-generated. The idempotency key that makes replays free. */
    clientUuid: z.string().uuid(),
    latitude: LatitudeSchema,
    longitude: LongitudeSchema,
    accuracy: AccuracySchema.nullable().optional(),
    altitude: AltitudeSchema.nullable().optional(),
    heading: HeadingSchema.nullable().optional(),
    speed: SpeedSchema.nullable().optional(),
    /** Device capture time. Must carry an offset — a naive timestamp is unusable. */
    recordedAt: IsoDateTimeSchema,
    batteryLevel: BatteryLevelSchema.nullable().optional(),
    isCharging: z.boolean().default(false),
    networkType: NetworkType.default('unknown'),
    /** false = captured while offline and queued. Not an error; expected. */
    isOnline: z.boolean().default(true),
    gpsEnabled: z.boolean().default(true),
    provider: LocationProvider.default('fused'),
    isMock: z.boolean().default(false),
    /** Bitmask of mock/root/emulator signals; see docs/05-TRACKING-ENGINE.md §5. */
    mockSignals: z.number().int().nonnegative().default(0),
    motionType: MotionType.default('unknown'),
  })
  .strict();
export type Point = z.infer<typeof PointSchema>;

/** Volatile device state, sent alongside a batch rather than per point. */
export const DeviceStateSchema = z.object({
  batteryLevel: BatteryLevelSchema.nullable().optional(),
  isCharging: z.boolean().default(false),
  networkType: NetworkType.default('unknown'),
  gpsEnabled: z.boolean().default(true),
  /** Device-reported unsynced count — how far behind this device is. */
  outboxPending: z.number().int().nonnegative().default(0),
  trackingStatus: z.enum(['active', 'paused', 'stopped', 'unknown']).default('unknown'),
});
export type DeviceState = z.infer<typeof DeviceStateSchema>;

export const MAX_BATCH_SIZE = 200;

export const PointBatchRequestSchema = z
  .object({
    batchUuid: z.string().uuid(),
    sessionUuid: z.string().uuid(),
    deviceState: DeviceStateSchema.optional(),
    points: z.array(PointSchema).min(1).max(MAX_BATCH_SIZE),
  })
  .strict();
export type PointBatchRequest = z.infer<typeof PointBatchRequestSchema>;

export const RejectedPointSchema = z.object({
  clientUuid: z.string().uuid(),
  reason: RejectReason,
  detail: z.string().optional(),
});
export type RejectedPoint = z.infer<typeof RejectedPointSchema>;

/**
 * What an admin can ask a device to do.
 *
 * There is no push channel yet (FCM is M8), so commands ride back on responses
 * the device is already making — a point upload or a heartbeat. That makes
 * delivery eventual, not instant, and the admin UI says so rather than
 * pretending otherwise.
 */
export const CommandType = z.enum([
  /** Open a tracking session and start the foreground service. */
  'start_tracking',
  /** End the session and stop the service. */
  'stop_tracking',
  /** Capture one fresh fix immediately and upload it, bypassing the filters. */
  'locate_now',
  /** Collect and upload call-log metadata now, rather than on the next tick. */
  'sync_call_logs',
  /** Drain the outbox now rather than on the next tick. */
  'force_sync',
  'refetch_config',
  'resync_geofences',
  /**
   * Begin microphone capture and upload it in ~60 s segments.
   *
   * A no-op on the device unless the employee has consented to voice capture
   * AND granted RECORD_AUDIO — the same two-gate pattern as call logs — and
   * Android shows its microphone indicator throughout. It cannot record
   * silently, by design and by OS enforcement.
   */
  'start_mic_capture',
  /** Stop microphone capture. Idempotent; stopping when idle does nothing. */
  'stop_mic_capture',
]);
export type CommandType = z.infer<typeof CommandType>;

/**
 * An instruction the server piggybacks onto a response.
 *
 * Declared once and reused by every device-facing response, so an ingest reply
 * and a heartbeat reply can never disagree about what a command looks like.
 *
 * `id` is what makes delivery safe. Commands are NOT removed when they are
 * handed to a device — they are removed when the device acknowledges the id,
 * exactly as the outbox deletes a point only on acknowledgement. A response
 * lost in flight therefore redelivers rather than vanishing, and every command
 * type is idempotent so a redelivery is harmless.
 */
export const ServerCommandSchema = z.object({
  id: z.string().uuid(),
  type: CommandType,
  reason: z.string().max(200).optional(),
  issuedAt: IsoDateTimeSchema,
});
export type ServerCommand = z.infer<typeof ServerCommandSchema>;

/**
 * The app-unlock PIN, in the only form that ever leaves the server.
 *
 * A derivation, never the PIN. It travels because verification has to happen
 * ON the device: a worker in a basement still needs to unlock, and a check that
 * required the network would lock people out exactly when the app matters most.
 *
 * What it is worth, stated plainly so nobody over-trusts it: four digits is ten
 * thousand possibilities, and anyone able to read the device's storage can
 * exhaust them against this hash almost instantly, whatever the iteration
 * count. It is a gate against someone casually picking up the phone. The
 * location data is protected by the bearer token in the Keystore and by
 * row-level security, not by this.
 */
export const PinPolicySchema = z.object({
  /** Bumped on every change, so a device can tell its copy is stale. */
  version: z.number().int(),
  /** base64 PBKDF2-HMAC-SHA256 output. */
  hash: z.string(),
  salt: z.string(),
  iterations: z.number().int().positive(),
});
export type PinPolicy = z.infer<typeof PinPolicySchema>;

/** Digits only: the app shows a numeric keypad, and a PIN with letters in it is a password. */
export const PinSchema = z.string().regex(/^\d{4,8}$/, 'The PIN must be 4 to 8 digits');

export const SetPinRequestSchema = z.object({ pin: PinSchema }).strict();
export type SetPinRequest = z.infer<typeof SetPinRequestSchema>;

/** What an admin sends to queue one. */
export const IssueCommandRequestSchema = z
  .object({
    type: CommandType,
    /** Recorded and shown to the employee; an audit trail for a remote action. */
    reason: z.string().max(200).optional(),
  })
  .strict();
export type IssueCommandRequest = z.infer<typeof IssueCommandRequestSchema>;

/**
 * A queued command and how far it has got.
 *
 * The admin needs all three states. "Stop tracking" that silently never
 * reached a phone is the same class of failure as a tracker that dies quietly:
 * the UI must be able to say `queued` (device has not checked in yet) rather
 * than implying the device has stopped.
 */
export const CommandStatusSchema = z.object({
  id: z.string().uuid(),
  type: CommandType,
  reason: z.string().nullable(),
  status: z.enum(['queued', 'delivered']),
  issuedAt: z.string(),
  issuedBy: z.number().int().nullable(),
  deliveredAt: z.string().nullable(),
  expiresAt: z.string(),
});
export type CommandStatus = z.infer<typeof CommandStatusSchema>;

/**
 * Per-point disposition.
 *
 * The client deletes exactly the union of accepted ∪ duplicate ∪ rejected from
 * its outbox. Any clientUuid absent from all three stays queued and is retried,
 * so a point can never be silently lost — that invariant is what makes the
 * whole offline story work, and it is asserted by an exhaustiveness test.
 */
export const BatchResultSchema = z.object({
  batchUuid: z.string().uuid(),
  accepted: z.array(z.string().uuid()),
  duplicate: z.array(z.string().uuid()),
  rejected: z.array(RejectedPointSchema),
  session: z.object({
    id: z.number().int(),
    distanceMeters: z.number().int(),
    lastPointAt: z.string().nullable(),
    acceptedPoints: z.number().int(),
  }),
  serverTime: z.string(),
  /** Server-issued instructions, applied on the next upload without a push. */
  commands: z.array(ServerCommandSchema),
});
export type BatchResult = z.infer<typeof BatchResultSchema>;

/**
 * A phone call recorded on a company device, as the device reports it.
 *
 * Metadata only — who, when, which direction, how long. Never call content:
 * Android blocks third-party call recording from the OS up, and recording
 * content is a different legal matter entirely. This is the same class of data
 * an itemised phone bill carries, collected on a company handset with the
 * employee's consent at enrolment.
 *
 * The direction values mirror Android's CallLog.Calls TYPE column. 'unknown'
 * exists because OEMs add non-standard types, and a call we cannot classify is
 * still a call that happened — dropping it would undercount activity.
 */
export const CallDirection = z.enum([
  'incoming',
  'outgoing',
  'missed',
  'rejected',
  'blocked',
  'voicemail',
  'unknown',
]);
export type CallDirection = z.infer<typeof CallDirection>;

export const CallLogEntrySchema = z
  .object({
    /**
     * Deterministic idempotency key, derived on the device from the call's
     * fixed facts (number + start + duration + direction). The call log carries
     * no stable id across reads, so a hash of what cannot change is what makes a
     * re-read of an overlapping window a no-op rather than a duplicate.
     */
    clientUuid: z.string().uuid(),
    /** The other party. Empty when withheld/private — kept, not dropped. */
    number: z.string().max(40),
    /** Cached contact name if the number was in the phone's contacts. */
    contactName: z.string().max(120).nullable().optional(),
    direction: CallDirection,
    /** When the call started, device-local clock with offset. */
    startedAt: IsoDateTimeSchema,
    /** Connected duration in seconds; 0 for missed/rejected. */
    durationSeconds: z.number().int().nonnegative(),
    /** SIM slot on a dual-SIM handset, when the OS reports it. */
    simSlot: z.number().int().nullable().optional(),
  })
  .strict();
export type CallLogEntry = z.infer<typeof CallLogEntrySchema>;

export const MAX_CALL_LOG_BATCH = 200;

export const CallLogBatchRequestSchema = z
  .object({
    batchUuid: z.string().uuid(),
    entries: z.array(CallLogEntrySchema).min(1).max(MAX_CALL_LOG_BATCH),
  })
  .strict();
export type CallLogBatchRequest = z.infer<typeof CallLogBatchRequestSchema>;

/**
 * Per-entry disposition, exactly like the point batch: the device deletes the
 * union of accepted ∪ duplicate from its outbox, and anything absent stays
 * queued and retries. A call log can never be silently lost.
 */
export const CallLogBatchResultSchema = z.object({
  batchUuid: z.string().uuid(),
  accepted: z.array(z.string().uuid()),
  duplicate: z.array(z.string().uuid()),
  serverTime: z.string(),
});
export type CallLogBatchResult = z.infer<typeof CallLogBatchResultSchema>;

/** One employee's call activity for a day, for the admin panel. */
export const CallActivityDtoSchema = z.object({
  employeeId: z.number().int(),
  date: z.string(),
  timezone: z.string(),
  summary: z.object({
    total: z.number().int(),
    outgoing: z.number().int(),
    incoming: z.number().int(),
    missed: z.number().int(),
    totalDurationSeconds: z.number().int(),
  }),
  calls: z.array(
    z.object({
      number: z.string(),
      contactName: z.string().nullable(),
      direction: CallDirection,
      startedAt: z.string(),
      durationSeconds: z.number().int(),
    }),
  ),
});
export type CallActivityDto = z.infer<typeof CallActivityDtoSchema>;

/**
 * An employee's call activity across a date range (inclusive, in the company's
 * timezone), for the bulk admin view. Same call/summary shapes as the per-day
 * DTO — the admin filters and groups client-side.
 */
export const CallRangeDtoSchema = z.object({
  employeeId: z.number().int(),
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  summary: CallActivityDtoSchema.shape.summary,
  calls: CallActivityDtoSchema.shape.calls,
});
export type CallRangeDto = z.infer<typeof CallRangeDtoSchema>;

/** One day's location summary within a range. */
export const DaySummarySchema = z.object({
  date: z.string(),
  pointCount: z.number().int(),
  distanceMeters: z.number(),
  movingSeconds: z.number().int(),
  trackedSeconds: z.number().int(),
  stopCount: z.number().int(),
  longStopCount: z.number().int(),
  firstPointAt: z.string().nullable(),
  lastPointAt: z.string().nullable(),
});
export type DaySummary = z.infer<typeof DaySummarySchema>;

/**
 * Per-day location summaries across a date range, for the bulk admin view.
 * Compact by design — one row per day, no raw points — so 30 days stays cheap.
 */
export const DailySummaryDtoSchema = z.object({
  employeeId: z.number().int(),
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  totals: z.object({
    distanceMeters: z.number(),
    pointCount: z.number().int(),
    stopCount: z.number().int(),
    activeDays: z.number().int(),
  }),
  days: z.array(DaySummarySchema),
});
export type DailySummaryDto = z.infer<typeof DailySummaryDtoSchema>;

/**
 * Proof of life from a device that may have nothing to upload.
 *
 * Without it, `devices.last_seen_at` only moves when points arrive, so a phone
 * whose location stream has died is indistinguishable from one that is switched
 * off — and both look identical to a phone standing still in a warehouse. The
 * heartbeat separates "the app is running and cannot get a fix" from "the app
 * is gone", which is the difference between a support call and a data gap
 * nobody notices.
 *
 * Sent on the service's own tick, so it also arrives while the outbox is empty.
 */
export const HeartbeatRequestSchema = z
  .object({
    /** Present while a shift is open; absent between shifts. */
    sessionUuid: z.string().uuid().nullable().optional(),
    deviceState: DeviceStateSchema,
    /**
     * The newest fix the device HOLDS, uploaded or not. Compared against the
     * newest fix the server has received to separate "not capturing" from
     * "capturing but cannot upload".
     */
    lastPointAt: IsoDateTimeSchema.nullable().optional(),
    /** Mirrors the permission report, so the admin can see why a device is silent. */
    permissions: z
      .object({
        location: z.enum(['always', 'while_in_use', 'denied']),
        notifications: z.boolean().default(false),
        batteryUnrestricted: z.boolean().default(false),
      })
      .optional(),
    appVersion: z.string().max(20).optional(),
    /**
     * Commands this device has carried out. Acknowledged ids are dropped from
     * the queue; anything unacknowledged is redelivered on the next exchange.
     */
    acknowledgedCommands: z.array(z.string().uuid()).max(50).default([]),
    /**
     * The PIN policy version this device holds. The server returns a fresh
     * policy only when it differs, so a change propagates within one heartbeat
     * without shipping the derivation on every exchange.
     */
    pinVersion: z.number().int().nullable().optional(),
  })
  .strict();
export type HeartbeatRequest = z.infer<typeof HeartbeatRequestSchema>;

export const HeartbeatResponseSchema = z.object({
  serverTime: z.string(),
  /** Echoed so the device can align its own watchdog with the server's view. */
  staleAfterSeconds: z.number().int(),
  /** True when the server has an open session the device did not name. */
  sessionMismatch: z.boolean(),
  commands: z.array(ServerCommandSchema),
  /** Present only when the device's `pinVersion` is behind. */
  pin: PinPolicySchema.nullable().optional(),
});
export type HeartbeatResponse = z.infer<typeof HeartbeatResponseSchema>;

export const StartSessionRequestSchema = z
  .object({
    /** Device-generated so a session can begin while offline. */
    sessionUuid: z.string().uuid(),
    startedAt: IsoDateTimeSchema,
    batteryLevel: BatteryLevelSchema.nullable().optional(),
    appVersion: z.string().max(20).optional(),
    osVersion: z.string().max(20).optional(),
  })
  .strict();
export type StartSessionRequest = z.infer<typeof StartSessionRequestSchema>;

export const EndSessionRequestSchema = z
  .object({
    endedAt: IsoDateTimeSchema.optional(),
    reason: z
      .enum([
        'shift_end',
        'manual',
        'permission_revoked',
        'logout',
        'auto_timeout',
        /**
         * Closed because an admin issued `stop_tracking`.
         *
         * The database enum has carried this value all along; the request
         * schema did not, so the device's end-of-session call was rejected as
         * invalid and swallowed by its own error handling. The shift stopped on
         * the phone and stayed open on the server — the admin saw a session
         * that never closed, with nothing in any log to say why.
         */
        'admin_stop',
      ])
      .default('manual'),
  })
  .strict();
export type EndSessionRequest = z.infer<typeof EndSessionRequestSchema>;

export const SessionDtoSchema = z.object({
  id: z.number().int(),
  sessionUuid: z.string().uuid(),
  status: z.enum(['active', 'ended', 'stale', 'force_closed']),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  acceptedPoints: z.number().int(),
  rejectedPoints: z.number().int(),
  distanceMeters: z.number().int(),
  lastPointAt: z.string().nullable(),
});
export type SessionDto = z.infer<typeof SessionDtoSchema>;

/**
 * Server-driven tracking policy.
 *
 * The most important operational endpoint in the system: it lets cadence,
 * accuracy thresholds and battery behaviour be tuned per tenant without
 * shipping an APK. The app caches it and re-fetches when `configVersion` changes.
 */
export const TrackingConfigSchema = z.object({
  configVersion: z.number().int(),
  mode: z.enum(['shift_only', 'always', 'manual', 'disabled']),
  profiles: z.record(
    z.object({
      intervalSeconds: z.number().int().positive(),
      distanceFilterMeters: z.number().int().nonnegative(),
      desiredAccuracy: z.enum(['lowest', 'low', 'medium', 'high', 'highest']),
    }),
  ),
  displacementSkipMeters: z.number().int().nonnegative(),
  accuracyRejectMeters: z.number().positive(),
  maxSpeedMps: z.number().positive(),
  batterySaver: z.object({
    thresholds: z.array(
      z.object({
        belowPercent: z.number().int(),
        intervalMultiplier: z.number().positive(),
        heartbeatOnly: z.boolean().default(false),
      }),
    ),
    ignoreWhenCharging: z.boolean(),
  }),
  upload: z.object({
    batchSize: z.number().int().positive(),
    maxBatchSize: z.number().int().positive(),
    intervalSeconds: z.number().int().positive(),
    retryBackoffSeconds: z.array(z.number().int().positive()),
    outboxMaxPoints: z.number().int().positive(),
  }),
  heartbeatIntervalSeconds: z.number().int().positive(),
  staleSessionSeconds: z.number().int().positive(),
});
export type TrackingConfig = z.infer<typeof TrackingConfigSchema>;

/**
 * Where an employee stopped, and for how long.
 *
 * Derived from the trail rather than reported by the device: the phone knows it
 * is still, but only the server can tell "still for eight minutes at a traffic
 * light" from "parked at the depot for two hours". See
 * docs/05-TRACKING-ENGINE.md §6.4.
 */
export const StopDtoSchema = z.object({
  /** 0-based ordinal within the day — the number on the map pin. */
  index: z.number().int(),
  startedAt: z.string(),
  endedAt: z.string(),
  durationSeconds: z.number().int(),
  /** Centroid of the fixes making up the stop. */
  latitude: z.number(),
  longitude: z.number(),
  /** How tight the stop was: the furthest fix from that centroid. */
  radiusMeters: z.number().int(),
  pointCount: z.number().int(),
  /** `long_stop` at or beyond 30 minutes — the one worth asking about. */
  type: z.enum(['stop', 'long_stop']),
  /** The trail ends here: they had not left when we last heard from the device. */
  isOpen: z.boolean(),
  /**
   * Reverse-geocoded address, when a provider is configured and the lookup
   * succeeded. Null is normal and never blocks the response — a stop with
   * coordinates and no street name is still a stop.
   */
  place: z.string().nullable(),
});
export type StopDto = z.infer<typeof StopDtoSchema>;

/** One run of the day: travelling, pausing, stopped, or unaccounted for. */
export const MovementSegmentSchema = z.object({
  kind: z.enum(['moving', 'idle', 'stopped', 'gap']),
  from: z.string(),
  to: z.string(),
  seconds: z.number().int(),
  distanceMeters: z.number().int(),
  avgSpeedMps: z.number().nullable(),
  maxSpeedMps: z.number().nullable(),
  /** Straight-line course over the run, degrees clockwise from true north. */
  bearing: z.number().nullable(),
  /** 16-point label for that bearing, e.g. 'NNE'. */
  compass: z.string().nullable(),
  /** Set on `stopped` runs: which stop this time was spent inside. */
  stopIndex: z.number().int().nullable(),
  /**
   * Inclusive bounds into `RouteDto.points`.
   *
   * What lets the map draw the trail per segment — solid where someone
   * travelled, dashed across a gap — instead of one undifferentiated line that
   * asserts they drove the straight path through an outage.
   */
  fromIndex: z.number().int(),
  toIndex: z.number().int(),
});
export type MovementSegment = z.infer<typeof MovementSegmentSchema>;

/** The day's movement totals. Definitions in docs/05-TRACKING-ENGINE.md §8. */
export const MovementSummarySchema = z.object({
  /** Elapsed time the trail actually covers — gaps excluded. */
  trackedSeconds: z.number().int(),
  movingSeconds: z.number().int(),
  /** Still, but too briefly to be a stop: traffic, doorways, a queue. */
  idleSeconds: z.number().int(),
  stoppedSeconds: z.number().int(),
  /** idle + stopped: total not-moving time. */
  stationarySeconds: z.number().int(),
  /** Time the device reported nothing. Shown, never absorbed into the others. */
  gapSeconds: z.number().int(),
  /**
   * Distance over the trail, excluding the hop across a gap. Differs slightly
   * from `RouteDto.summary.distanceMeters` (and from the session rollup), which
   * counts every hop because it is accumulated at ingest time, before anyone
   * can know a gap was opening.
   */
  distanceMeters: z.number().int(),
  /** Excludes accuracy drift accrued while parked. */
  movingDistanceMeters: z.number().int(),
  /** Moving distance ÷ moving time — not distance ÷ elapsed. */
  avgSpeedMps: z.number().nullable(),
  /** Filtered to accurate fixes over real intervals; raw maxima are GPS noise. */
  maxSpeedMps: z.number().nullable(),
  maxSpeedAt: z.string().nullable(),
  stopCount: z.number().int(),
  longStopCount: z.number().int(),
  longestStopSeconds: z.number().int(),
});
export type MovementSummary = z.infer<typeof MovementSummarySchema>;

/** A position as the admin live view consumes it. */
export const LiveEmployeeSchema = z.object({
  employee: z.object({
    id: z.number().int(),
    name: z.string(),
    code: z.string(),
    designation: z.string().nullable(),
  }),
  position: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
      accuracy: z.number().nullable(),
      heading: z.number().nullable(),
      speed: z.number().nullable(),
      recordedAt: z.string(),
      /** Staleness in seconds. Above staleThresholdSeconds the device is offline. */
      ageSeconds: z.number().int(),
    })
    .nullable(),
  state: z.object({
    batteryLevel: z.number().int().nullable(),
    isCharging: z.boolean(),
    networkType: NetworkType,
    gpsEnabled: z.boolean(),
    isMock: z.boolean(),
    motionType: MotionType,
    trackingStatus: z.enum(['active', 'paused', 'stopped', 'unknown']),
    outboxPending: z.number().int(),
    /**
     * Last proof of life, which moves even when no points arrive. A fresh
     * heartbeat next to a stale position is the signature of a live app that
     * has lost its location stream — the failure that used to be invisible.
     */
    lastSeenAt: z.string().nullable(),
    /** `none` when the employee has no session today. */
    sessionStatus: z.enum(['active', 'stale', 'ended', 'force_closed', 'none']),
    /**
     * How far behind the device's last upload was running: the age of the
     * oldest fix in it when it arrived.
     *
     * `outboxPending` says how MUCH is waiting; this says how OLD it is, and
     * the two answer different questions. Fifty points queued is a blip if they
     * are a minute old and a four-hour hole in the record if they are not.
     * Null when the device has never uploaded a batch.
     */
    syncLagSeconds: z.number().int().nullable(),
  }),
  /**
   * Are they moving, and which way.
   *
   * `bearing` is the course over the last stretch of real displacement, not the
   * `heading` of a single fix — a phone in a pocket reports whichever way the
   * handset happened to be pointing, which is noise. Null when there has not
   * been enough movement to have a direction, rather than a guess.
   */
  movement: z.object({
    state: z.enum(['moving', 'stationary', 'unknown']),
    speedMps: z.number().nullable(),
    bearing: z.number().nullable(),
    /** 16-point label for that bearing, e.g. 'NNE'. */
    compass: z.string().nullable(),
  }),
  /**
   * Where they have been sitting still, and since when.
   *
   * Maintained live as points arrive: the anchor moves when a fix lands outside
   * the dwell radius, so `since` is the moment they last actually went
   * somewhere.
   *
   * Two rules keep it from overstating. `seconds` is measured to the newest
   * FIX, not to now — a phone that went offline an hour ago has not been
   * observed standing still for that hour, and the position age is reported
   * separately so the two are never confused. And a silence longer than the gap
   * threshold restarts the clock, so a device that went quiet at a depot and
   * came back to the same spot reports the time it was actually seen there
   * rather than folding the outage in.
   */
  dwell: z
    .object({
      since: z.string(),
      seconds: z.number().int(),
      latitude: z.number(),
      longitude: z.number(),
      radiusMeters: z.number().int(),
      /** Reverse-geocoded, when a provider is configured. Null is normal. */
      place: z.string().nullable(),
    })
    .nullable(),
  today: z.object({
    distanceMeters: z.number().int(),
    pointCount: z.number().int(),
  }),
  /** e.g. ['battery_low', 'gps_disabled', 'stale', 'mock_gps'] */
  flags: z.array(z.string()),
});
export type LiveEmployee = z.infer<typeof LiveEmployeeSchema>;

/**
 * How a fix reached the server — the distinction between "we knew at the time"
 * and "we found out later".
 *
 * Derived from `received_at − recorded_at`, NOT from the device's own
 * `is_online` flag, and the difference matters. `is_online` records whether a
 * network interface was up at the moment of capture, which is a claim about the
 * phone; the lag records when the row actually landed in Postgres, which is a
 * claim about what the admin could have known. They disagree often enough to
 * matter: a captive portal, an expired token, a dead cell showing four bars and
 * a server outage all produce points flagged `is_online = true` that arrived
 * hours late. Lag cannot lie about that, because it is measured at both ends.
 *
 *  • `live`     — on the map within LIVE_DELIVERY_MAX_LAG_SECONDS of capture.
 *  • `delayed`  — late, but inside the same working rhythm: a slow drain, a
 *                 brief dead spot, one failed batch that retried.
 *  • `backfill` — the case this classification exists for: a device that was
 *                 out of contact and dumped its outbox on reconnection.
 */
export const PointDelivery = z.enum(['live', 'delayed', 'backfill']);
export type PointDelivery = z.infer<typeof PointDelivery>;

/**
 * At or under this lag a fix counts as live.
 *
 * Three minutes rather than one, because the device's own upload tick is 60s
 * and a batch that misses one tick is still, operationally, a live feed. A
 * tighter bound would paint an ordinary healthy device amber for a third of its
 * points and the colour would stop meaning anything.
 */
export const LIVE_DELIVERY_MAX_LAG_SECONDS = 180;

/**
 * Above this lag a fix is a backfill.
 *
 * Fifteen minutes is the point past which the admin's picture was materially
 * wrong while it lasted: a dispatcher looking at the map ten minutes ago would
 * have seen this employee somewhere they were not.
 */
export const BACKFILL_MIN_LAG_SECONDS = 900;

/** Classifies one fix's lag. Shared so the API and the panel cannot disagree. */
export function classifyDelivery(lagSeconds: number | null): PointDelivery {
  if (lagSeconds === null) return 'live';
  if (lagSeconds <= LIVE_DELIVERY_MAX_LAG_SECONDS) return 'live';
  if (lagSeconds < BACKFILL_MIN_LAG_SECONDS) return 'delayed';
  return 'backfill';
}

export const RoutePointSchema = z.object({
  /** Milliseconds since the first point — playback is pure interpolation over this. */
  t: z.number().int(),
  lat: z.number(),
  lng: z.number(),
  acc: z.number().nullable(),
  spd: z.number().nullable(),
  hdg: z.number().nullable(),
  bat: z.number().int().nullable(),
  mt: MotionType,
  recordedAt: z.string(),
  /** Seconds this fix waited on the device before the server had it. */
  lag: z.number().int(),
  /** The bucket that lag falls into. See PointDelivery. */
  dl: PointDelivery,
  /** false = the device had no network interface up when it captured this. */
  on: z.boolean(),
});
export type RoutePoint = z.infer<typeof RoutePointSchema>;

/**
 * A contiguous stretch of the trail that arrived the same way.
 *
 * Runs, not per-point styling, because the map draws polylines: a run is
 * exactly one `<Polyline>` and the client never has to group anything.
 */
export const DeliveryRunSchema = z.object({
  kind: PointDelivery,
  from: z.string(),
  to: z.string(),
  /** Inclusive bounds into `points`, so the map can slice and style the run. */
  fromIndex: z.number().int(),
  toIndex: z.number().int(),
  pointCount: z.number().int(),
  /** The worst lag in the run — how far behind reality this stretch was. */
  maxLagSeconds: z.number().int(),
  /**
   * When the server finally had the whole run. For a backfill this is the
   * moment the device came back and pushed, which is the timestamp an admin
   * asking "when did we learn this?" actually wants.
   */
  uploadedAt: z.string(),
});
export type DeliveryRun = z.infer<typeof DeliveryRunSchema>;

/**
 * One bulk push: a batch (or a burst of batches) that carried late data.
 *
 * Grouped by the arrival instant rather than by `sync_batch_id` alone, because
 * a device returning from four hours offline uploads its outbox 200 points at a
 * time and produces a dozen batch rows seconds apart. Those are one event to
 * the person reading the screen — "at 17:03 this phone dumped three hours of
 * history" — and reporting twelve would bury the fact in its own detail.
 */
export const UploadEpisodeSchema = z.object({
  /** ISO instant the push landed. */
  uploadedAt: z.string(),
  /** Earliest and latest capture time carried in the push. */
  coversFrom: z.string(),
  coversTo: z.string(),
  pointCount: z.number().int(),
  batchCount: z.number().int(),
  maxLagSeconds: z.number().int(),
  /** The worst class in the push: one backfilled point makes the push a backfill. */
  kind: PointDelivery,
});
export type UploadEpisode = z.infer<typeof UploadEpisodeSchema>;

export const RouteDtoSchema = z.object({
  employeeId: z.number().int(),
  /** The local calendar day this route covers, in the company's timezone. */
  date: z.string(),
  /** The timezone `date` is expressed in — an IANA name, e.g. 'Asia/Kolkata'. */
  timezone: z.string(),
  summary: z.object({
    pointCount: z.number().int(),
    rejectedCount: z.number().int(),
    distanceMeters: z.number().int(),
    maxSpeed: z.number().nullable(),
    firstPointAt: z.string().nullable(),
    lastPointAt: z.string().nullable(),
    sessionCount: z.number().int(),
    /** How many of these fixes were captured with no connectivity. */
    offlinePointCount: z.number().int(),
    /**
     * The longest a fix waited between being captured and reaching the server.
     * The headline number for "how far behind did this device get".
     */
    maxSyncLagSeconds: z.number().int(),
    /** Fixes that were on the map while they were still true. */
    livePointCount: z.number().int(),
    /** Late, but not so late the picture was wrong. */
    delayedPointCount: z.number().int(),
    /** Fixes the admin only learned about after the fact. */
    backfillPointCount: z.number().int(),
    /** Distinct bulk pushes that carried backfilled data. */
    backfillUploadCount: z.number().int(),
    /**
     * Seconds of the day for which the live map was showing a stale position
     * because the data had not arrived yet.
     *
     * Not the same as `gapSeconds`: a gap is time with no fix at all, this is
     * time with a fix that nobody could see. A device can have neither, either
     * or both.
     */
    darkSeconds: z.number().int(),
  }),
  /** Movement totals for the day. See MovementSummarySchema. */
  movement: MovementSummarySchema,
  /** Where they stood still long enough for it to be worth naming. */
  stops: z.array(StopDtoSchema),
  /**
   * The day broken into runs of travelling / pausing / stopped / silent.
   *
   * Every second between the first and last fix belongs to exactly one segment,
   * so the timeline the panel draws has no unexplained slack in it.
   */
  segments: z.array(MovementSegmentSchema),
  bounds: z
    .object({ north: z.number(), south: z.number(), east: z.number(), west: z.number() })
    .nullable(),
  points: z.array(RoutePointSchema),
  /**
   * Data gaps, rendered dashed rather than as a straight travelled line.
   * Presenting a gap as a road path would misrepresent where someone went.
   */
  gaps: z.array(z.object({ from: z.string(), to: z.string(), seconds: z.number().int() })),
  /**
   * Stretches captured while the device had no connectivity and uploaded later.
   *
   * The positions are exactly as trustworthy as any other — GPS comes from
   * satellites, not from the network, so a phone in a basement car park is
   * still fixing perfectly well — but WHEN THEY BECAME KNOWN is different, and
   * that distinction has consequences. "He was at the depot at 14:00" and "at
   * 17:00 we learned he had been at the depot at 14:00" support very different
   * decisions, and an admin looking at a trail cannot tell them apart unless
   * the panel says so. Marked on the map, never hidden.
   */
  offlineRuns: z.array(
    z.object({
      from: z.string(),
      to: z.string(),
      pointCount: z.number().int(),
      /** Inclusive bounds into `points`, so the map can style the run. */
      fromIndex: z.number().int(),
      toIndex: z.number().int(),
    }),
  ),
  /**
   * The same trail, cut by HOW each stretch reached us rather than by what the
   * employee was doing.
   *
   * `segments` answers "were they moving?"; this answers "did we know?". Both
   * partition the same point array and the map draws one or the other, because
   * two colour scales on one line is a legend nobody reads.
   */
  deliveryRuns: z.array(DeliveryRunSchema),
  /**
   * Every moment the day's data actually arrived, newest last.
   *
   * This is the audit trail behind the colours: a backfilled stretch on the map
   * has exactly one episode here saying when it landed and how much came with
   * it, so "why was he missing from the map all afternoon" has an answer with a
   * timestamp on it rather than a shrug.
   */
  uploads: z.array(UploadEpisodeSchema),
});
export type RouteDto = z.infer<typeof RouteDtoSchema>;
